import { DEFAULT_WEIGHTS, validateWeights } from './24-interest-signals.ts';
import type { RankingWeights } from './24-interest-signals.ts';
/** Pure experimental policy. Input eligibility MUST be established by a trusted server. */
export type PAD = readonly [number, number, number];
export type Mode = 'automatic' | 'calm' | 'connected' | 'inspired' | 'energized' | 'latest';
export type Tone = { pad: PAD; confidence: number };
export type Candidate = {
  id: string;
  authorId: string;
  topic: string;
  createdAt: number;
  /** Trusted maintained discovery UTC-day timestamp; Latest always uses createdAt. */
  discoveryCreatedAt?: number;
  eligible: boolean;
  moderation: 'ready' | 'pending' | 'removed';
  relationship: number;
  helpfulness: number;
  interest?: number;
  tone?: Tone;
};
export type Preference = {
  target: PAD;
  informativePostIds: readonly string[];
  updatedAt: number;
};
export type Feedback = 'helpful' | 'more' | 'too-intense' | 'like' | 'dwell';
export type Ranked = { post: Candidate; score: number; reason: string };

export const POLICY_VERSION = '0.5.0';
export const BALANCED: PAD = [0.3, 0, 0.1];
export const TARGETS: Readonly<Record<Exclude<Mode, 'automatic' | 'latest'>, PAD>> = {
  calm: [0.5, -0.6, 0.2],
  connected: [0.3, -0.1, 0.1],
  inspired: [0.65, 0.25, 0.4],
  energized: [0.6, 0.65, 0.3],
};
const DAY = 24 * 60 * 60 * 1000;

const clamp = (value: number, min = 0, max = 1) =>
  Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : min;

/** Validate scores at runtime: erased TypeScript types cannot validate provider output. */
export function validTone(tone?: Tone): tone is Tone {
  return (
    !!tone &&
    Array.isArray(tone.pad) &&
    tone.pad.length === 3 &&
    tone.pad.every((v) => Number.isFinite(v) && v >= -1 && v <= 1) &&
    Number.isFinite(tone.confidence) &&
    tone.confidence >= 0 &&
    tone.confidence <= 1
  );
}

/** PAD distance normalized by its maximum (sqrt(4+4+1)); dominance has quarter weight. */
export function fit(pad: PAD, target: PAD): number {
  const [p, a, d] = pad.map((v, i) => v - target[i]);
  return clamp(1 - Math.sqrt((p * p + a * a + 0.25 * d * d) / 9));
}

export function emptyPreference(now: number): Preference {
  return { target: BALANCED, informativePostIds: [], updatedAt: now };
}

function currentPreference(state: Preference | undefined, now: number): Preference {
  if (
    !state ||
    !Number.isFinite(state.updatedAt) ||
    state.updatedAt > now ||
    now - state.updatedAt >= DAY ||
    !validTone({ pad: state.target, confidence: 1 })
  ) {
    return emptyPreference(now);
  }
  return state;
}

/** Activate after three distinct posts in the retained signal window; expire after idle TTL. */
export function automaticTarget(state: Preference | undefined, now: number): PAD {
  const current = currentPreference(state, now);
  return new Set(current.informativePostIds).size >= 3 ? current.target : BALANCED;
}

/**
 * Conservative preference estimate, not mood detection. Ignores ambiguous behavior,
 * low reliability and repeated feedback. Updates are capped to 0.15 per axis.
 */
export function adapt(
  state: Preference | undefined,
  post: Candidate,
  feedback: Feedback,
  now: number,
  learningEnabled = true,
): Preference {
  const current = currentPreference(state, now);
  if (
    !learningEnabled ||
    !post.eligible ||
    post.moderation !== 'ready' ||
    !validTone(post.tone) ||
    post.tone.confidence < 0.6 ||
    feedback === 'like' ||
    feedback === 'dwell' ||
    current.informativePostIds.includes(post.id)
  )
    return current;

  const desired: PAD =
    feedback === 'too-intense'
      ? [current.target[0], current.target[1] - 0.15, current.target[2]]
      : post.tone.pad;
  const target = current.target.map((v, i) => {
    const movement = feedback === 'too-intense' ? desired[i] - v : (desired[i] - v) * 0.2;
    return clamp(v + clamp(movement, -0.15, 0.15), -1, 1);
  }) as unknown as PAD;
  return {
    target,
    informativePostIds: [...current.informativePostIds, post.id].slice(-64),
    updatedAt: now,
  };
}

/** Use an injected maintained discovery date in milliseconds; invalid/future overrides
 * abstain to the real post timestamp. Exact chronological ordering bypasses this helper. */
export function discoveryTime(post: Candidate): number {
  return Number.isFinite(post.discoveryCreatedAt) &&
    post.discoveryCreatedAt! >= 0 &&
    post.discoveryCreatedAt! <= post.createdAt
    ? post.discoveryCreatedAt!
    : post.createdAt;
}

function recency(a: Candidate, b: Candidate): number {
  return b.createdAt - a.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export type RankOptions = {
  mode: Mode;
  now: number;
  preference?: Preference;
  hidden?: ReadonlySet<string>;
  limit?: number;
  weights?: RankingWeights;
};

/** Score authorized candidates and greedily diversify a single batch. Eligibility
 * is established by the caller; time is a finite injected epoch timestamp.
 * Malformed identities/dates are excluded; invalid options throw RangeError. */
export function rank(candidates: readonly Candidate[], options: RankOptions): Ranked[] {
  if (!Number.isFinite(options.now)) throw new RangeError('now must be a finite epoch timestamp');
  const limit = options.limit ?? 20;
  if (!Number.isInteger(limit) || limit < 0 || limit > 100)
    throw new RangeError('limit must be 0..100');
  return selectBatch(scoreCandidates(candidates, options), limit, options.mode === 'latest');
}

/** Preserve repeated single-batch ranking exactly while preparing immutable scores
 * once per request. Author/topic diversity intentionally resets at every batch
 * boundary, matching the existing frozen feed order; this is not a new policy.
 * Batch size is1..100. No database authorization or cross-request cache lives here. */
export function rankBatches(
  candidates: readonly Candidate[],
  options: Omit<RankOptions, 'limit'> & { batchSize?: number },
): Ranked[] {
  if (!Number.isFinite(options.now)) throw new RangeError('now must be a finite epoch timestamp');
  const size = options.batchSize ?? 100;
  if (!Number.isInteger(size) || size < 1 || size > 100)
    throw new RangeError('batchSize must be1..100');
  const remaining = scoreCandidates(candidates, options);
  if (options.mode === 'latest') return remaining;
  const result: Ranked[] = [];
  while (remaining.length) result.push(...selectBatch(remaining, size, false));
  return result;
}

/** Prepare canonical eligible scores without a diversity/position decision. Callers
 * establish server authorization first. Inject finite epoch milliseconds; malformed
 * records are omitted and invalid mode/weights/time throw rather than sort NaN. */
export function scoreCandidates(candidates: readonly Candidate[], options: RankOptions): Ranked[] {
  if (!Number.isFinite(options.now)) throw new RangeError('now must be a finite epoch timestamp');
  if (
    !['automatic', 'calm', 'connected', 'inspired', 'energized', 'latest'].includes(options.mode)
  ) {
    throw new RangeError('unsupported feed mode');
  }
  const seen = new Set<string>();
  const weights = validateWeights(options.weights ?? DEFAULT_WEIGHTS);
  const eligible = candidates.filter((post) => {
    if (
      !post.id ||
      !post.authorId ||
      !post.eligible ||
      post.moderation !== 'ready' ||
      !Number.isFinite(post.createdAt) ||
      post.createdAt > options.now ||
      options.hidden?.has(post.id) ||
      seen.has(post.id)
    )
      return false;
    seen.add(post.id);
    return true;
  });
  if (options.mode === 'latest')
    return eligible.sort(recency).map((post) => ({
      post,
      score: 0,
      reason: 'Newest updates first',
    }));

  const target =
    options.mode === 'automatic'
      ? automaticTarget(options.preference, options.now)
      : TARGETS[options.mode];
  return eligible.map((post) => {
    const tone = validTone(post.tone) ? post.tone : undefined;
    const emotionFit = tone
      ? tone.confidence * fit(tone.pad, target) + (1 - tone.confidence) * 0.5
      : 0.5;
    const relationship = clamp(post.relationship);
    const freshness = Math.pow(2, -(options.now - discoveryTime(post)) / DAY);
    const helpfulness = clamp(post.helpfulness);
    return {
      post,
      score:
        weights.mood * emotionFit +
        weights.interests * clamp(post.interest ?? 0.5) +
        weights.relationships * relationship +
        weights.recency * freshness +
        weights.quality * helpfulness,
      reason: !tone
        ? 'Balanced placement; tone is not yet known'
        : options.mode === 'automatic'
          ? post.interest !== undefined && post.interest > 0.6
            ? 'Your interests, tone preference, connections and recent updates'
            : 'Tone preference, your connections, and recent updates'
          : `Fits your ${options.mode} choice, with connections and recent updates`,
    };
  });
}

/** Independent0..1 content suitability estimates, not probabilities or reader mood.
 * Uses the experimental PAD targets and blends uncertainty toward0.5. Invalid or
 * missing input abstains with null; author labels do not become calibrated analysis. */
export function contentToneScores(tone?: Tone): Record<keyof typeof TARGETS, number> | null {
  if (!validTone(tone)) return null;
  return Object.fromEntries(
    Object.entries(TARGETS).map(([mode, target]) => [
      mode,
      tone.confidence * fit(tone.pad, target) + (1 - tone.confidence) * 0.5,
    ]),
  ) as Record<keyof typeof TARGETS, number>;
}

function selectBatch(remaining: Ranked[], limit: number, chronological: boolean): Ranked[] {
  if (chronological) return remaining.splice(0, limit);
  const result: Ranked[] = [];
  while (remaining.length && result.length < limit) {
    const last = result.at(-1)?.post.authorId;
    const previous = result.at(-2)?.post.authorId;
    const preventThird =
      last && last === previous && remaining.some((item) => item.post.authorId !== last);
    const recentCount = Math.min(3, result.length);
    const topic0 = result.at(-1)?.post.topic;
    const topic1 = result.at(-2)?.post.topic;
    const topic2 = result.at(-3)?.post.topic;
    let bestIndex = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const item = remaining[i];
      if (preventThird && item.post.authorId === last) continue;
      // Strict equality matches the former filter, including malformed NaN topics.
      // Counting avoids allocating an array for every candidate at every selection.
      const topic = item.post.topic;
      const repeatedTopics =
        Number(recentCount > 0 && topic0 === topic) +
        Number(recentCount > 1 && topic1 === topic) +
        Number(recentCount > 2 && topic2 === topic);
      const adjusted = item.score - repeatedTopics * 0.08;
      if (
        adjusted > bestScore ||
        (adjusted === bestScore &&
          bestIndex >= 0 &&
          (discoveryTime(remaining[bestIndex].post) - discoveryTime(item.post) ||
            (item.post.id < remaining[bestIndex].post.id
              ? -1
              : item.post.id > remaining[bestIndex].post.id
                ? 1
                : 0)) < 0)
      ) {
        bestScore = adjusted;
        bestIndex = i;
      }
    }
    result.push(remaining.splice(bestIndex, 1)[0]);
  }
  return result;
}
